import React, { useState } from 'react';
import { PlansTab } from './PlansTab';
import { DosesTab } from './Doses/DosesTab';
import { CurrentPlan } from '../../types/data';
import { getCurrentPlans, saveCurrentPlan, deleteCurrentPlan, clearCurrentPlans } from '../../utils/storage/currentPlans';
import { downloadCSV, parseCSV } from '../../utils/csv';
import { currentPlans as currentPlansMapper } from '../../utils/csv/mappers';
import { generateCalendarEvents } from '../../utils/calendar';

export const CurrentPlans: React.FC = () => {
  const [currentPlans, setCurrentPlans] = useState<CurrentPlan[]>(getCurrentPlans());
  const [showForm, setShowForm] = useState(false);
  const [editingCurrentPlan, setEditingCurrentPlan] = useState<CurrentPlan | undefined>();
  const [activeTab, setActiveTab] = useState<'plans' | 'doses'>('plans');

  const handleSubmit = (currentPlan: CurrentPlan) => {
    saveCurrentPlan(currentPlan);
    setCurrentPlans(getCurrentPlans());
    setShowForm(false);
    setEditingCurrentPlan(undefined);
  };

  const handleEdit = (currentPlan: CurrentPlan) => {
    setEditingCurrentPlan(currentPlan);
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this plan?')) {
      deleteCurrentPlan(id);
      setCurrentPlans(getCurrentPlans());
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCurrentPlan(undefined);
  };

  const handleClear = () => {
    clearCurrentPlans();
    setCurrentPlans([]);
  };

  const handleExport = () => {
    const columns = currentPlansMapper.currentPlanHeaders.map(header => ({
      header,
      accessor: (item: CurrentPlan) => currentPlansMapper.toCSV(item)[header]
    }));
    downloadCSV(currentPlans, columns, 'current-plans');
  };

  const handleImport = async (csvData: string) => {
    try {
      const parsed = await parseCSV(csvData);
      const imported = parsed.map(row => currentPlansMapper.fromCSV(row));
      imported.forEach(item => saveCurrentPlan(item as CurrentPlan));
      setCurrentPlans(getCurrentPlans());
    } catch (error) {
      console.error('Failed to import current plans:', error);
      alert('Failed to import CSV file. Please check the file format.');
    }
  };

  const handleGenerateCalendar = () => {
    try {
      const icsContent = generateCalendarEvents(currentPlans);
      const blob = new Blob([icsContent], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'current-plans.ics';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to generate calendar:', error);
      alert('Failed to generate calendar. Please check your current plans data.');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Tabs Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-4">
          <button
            onClick={() => setActiveTab('plans')}
            className={`py-2 px-4 font-medium text-sm ${
              activeTab === 'plans'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Plans
          </button>
          <button
            onClick={() => setActiveTab('doses')}
            className={`py-2 px-4 font-medium text-sm ${
              activeTab === 'doses'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Doses Required
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'plans' && (
        <PlansTab
          currentPlans={currentPlans}
          showForm={showForm}
          editingCurrentPlan={editingCurrentPlan}
          onSubmit={handleSubmit}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onCancel={handleCancel}
          onClear={handleClear}
          onExport={handleExport}
          onImport={handleImport}
          onGenerateCalendar={handleGenerateCalendar}
          setShowForm={setShowForm}
        />
      )}
      {activeTab === 'doses' && <DosesTab />}
    </div>
  );
};
